import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Building2, User, Mail, DollarSign, Clock, FileText, TrendingDown } from "lucide-react";
import { formatFullDateTime } from "@/lib/timezone";

interface ProposalDetail {
  id: string;
  supplier_company_name: string;
  contact_name: string;
  contact_email: string;
  initial_bid_amount: number;
  proposal_details: string | null;
  submitted_at: string;
  status: string;
  selection_status: string;
  question_responses: Record<string, string> | null;
  rfp: {
    id: string;
    title: string;
    matter_name: string | null;
    description: string;
  };
  bids: {
    id: string;
    amount: number;
    created_at: string;
    source: string;
    status: string;
  }[];
}

const ProposalDetail = () => {
  const { id: rfpId, proposalId } = useParams<{ id: string; proposalId: string }>();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<ProposalDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (proposalId && rfpId) {
      fetchProposalDetail();
    }
  }, [proposalId, rfpId]);

  const fetchProposalDetail = async () => {
    try {
      // Fetch proposal with RFP details
      const { data: proposalData, error: proposalError } = await supabase
        .from("proposals")
        .select(`
          *,
          rfp:rfps(id, title, matter_name, description)
        `)
        .eq("id", proposalId)
        .single();

      if (proposalError) throw proposalError;

      // Fetch bids for this proposal
      const { data: bidsData, error: bidsError } = await supabase
        .from("bids")
        .select("*")
        .eq("proposal_id", proposalId)
        .order("created_at", { ascending: false });

      if (bidsError) throw bidsError;

      setProposal({
        ...proposalData,
        rfp: proposalData.rfp,
        bids: bidsData || [],
        question_responses: proposalData.question_responses as Record<string, string> | null,
      });
    } catch (error) {
      console.error("Error fetching proposal:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentBid = () => {
    if (proposal?.bids && proposal.bids.length > 0) {
      return Math.min(...proposal.bids.map(b => b.amount));
    }
    return proposal?.initial_bid_amount || 0;
  };

  const getSavings = () => {
    if (!proposal) return 0;
    const currentBid = getCurrentBid();
    return proposal.initial_bid_amount - currentBid;
  };

  const getSavingsPercent = () => {
    if (!proposal) return 0;
    const savings = getSavings();
    return (savings / proposal.initial_bid_amount) * 100;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Proposal not found</p>
        <Button onClick={() => navigate(`/auctions/${rfpId}/bids`)} className="mt-4">
          Back to Auction
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
      <Button variant="ghost" size="icon" onClick={() => navigate("/active")}>
        <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-foreground">Proposal Details</h2>
          <p className="text-muted-foreground">
            {proposal.rfp.matter_name || proposal.rfp.title}
          </p>
        </div>
        <Badge variant={proposal.selection_status === "successful" ? "default" : "secondary"}>
          {proposal.selection_status === "successful" ? "Winner" : proposal.selection_status}
        </Badge>
      </div>

      {/* Supplier Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Supplier Information
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Company Name</p>
                <p className="text-lg font-semibold">{proposal.supplier_company_name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Contact Name</p>
                <p className="text-foreground">{proposal.contact_name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Contact Email</p>
                <p className="text-foreground">{proposal.contact_email}</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Submitted At</p>
                <p className="text-foreground">{formatFullDateTime(proposal.submitted_at)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <Badge variant="outline">{proposal.status}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bid Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Bid Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-4">
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-sm text-muted-foreground mb-1">Initial Bid</p>
              <p className="text-2xl font-bold">${proposal.initial_bid_amount.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg text-center">
              <p className="text-sm text-green-600 mb-1">Current Bid</p>
              <p className="text-2xl font-bold text-green-700">${getCurrentBid().toLocaleString()}</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-center">
              <p className="text-sm text-blue-600 mb-1">Total Savings</p>
              <p className="text-2xl font-bold text-blue-700">${getSavings().toLocaleString()}</p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg text-center">
              <p className="text-sm text-purple-600 mb-1">Savings %</p>
              <p className="text-2xl font-bold text-purple-700 flex items-center justify-center gap-1">
                <TrendingDown className="h-5 w-5" />
                {getSavingsPercent().toFixed(1)}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Proposal Details Card - Structured Layout */}
      {proposal.proposal_details && (
        <Card>
          <CardHeader>
            <CardTitle>Proposal Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {(() => {
              try {
                const details = typeof proposal.proposal_details === 'string' 
                  ? JSON.parse(proposal.proposal_details) 
                  : proposal.proposal_details;
                
                return (
                  <div className="grid gap-6 md:grid-cols-1">
                    {details.summary && (
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Summary
                        </h4>
                        <p className="text-foreground whitespace-pre-wrap">{details.summary}</p>
                      </div>
                    )}
                    
                    {details.scope && (
                      <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                        <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-2">
                          Scope of Work
                        </h4>
                        <p className="text-foreground whitespace-pre-wrap line-clamp-2">
                          {details.scope}
                        </p>
                      </div>
                    )}
                    
                    {details.deliverables && (
                      <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg">
                        <h4 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2">
                          Deliverables
                        </h4>
                        <p className="text-foreground whitespace-pre-wrap">{details.deliverables}</p>
                      </div>
                    )}
                    
                    {details.exemptions && (
                      <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                        <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">
                          Exemptions / Exclusions
                        </h4>
                        <p className="text-foreground whitespace-pre-wrap">{details.exemptions}</p>
                      </div>
                    )}
                    
                    {details.assumptions && (
                      <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                        <h4 className="text-sm font-semibold text-purple-700 dark:text-purple-400 mb-2">
                          Assumptions
                        </h4>
                        <p className="text-foreground whitespace-pre-wrap">{details.assumptions}</p>
                      </div>
                    )}
                    
                    {details.timeline && (
                      <div className="p-4 bg-cyan-50 dark:bg-cyan-950/30 rounded-lg">
                        <h4 className="text-sm font-semibold text-cyan-700 dark:text-cyan-400 mb-2">
                          Timeline
                        </h4>
                        <p className="text-foreground whitespace-pre-wrap">{details.timeline}</p>
                      </div>
                    )}
                  </div>
                );
              } catch {
                // Fallback for plain text
                return (
                  <p className="text-foreground whitespace-pre-wrap">{proposal.proposal_details}</p>
                );
              }
            })()}
          </CardContent>
        </Card>
      )}

      {/* Question Responses Card */}
      {proposal.question_responses && Object.keys(proposal.question_responses).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Question Responses</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(proposal.question_responses).map(([question, answer], index) => (
              <div key={index} className="border-b border-border pb-4 last:border-0 last:pb-0">
                <p className="text-sm font-medium text-muted-foreground mb-1">{question}</p>
                <p className="text-foreground">{answer}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Bid History Card */}
      {proposal.bids && proposal.bids.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Bid History ({proposal.bids.length} bids)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {proposal.bids.map((bid) => (
                <div
                  key={bid.id}
                  className="p-4 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xl font-bold text-green-600">${bid.amount.toLocaleString()}</p>
                    <Badge 
                      variant={bid.status === 'requested' ? 'secondary' : 'outline'} 
                      className={`text-xs ${bid.status === 'requested' ? 'bg-orange-100 text-orange-700' : ''}`}
                    >
                      {bid.status === 'requested' ? 'Requested Offer' : bid.source.replace(/_/g, " ")}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {formatFullDateTime(bid.created_at)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Back Button Footer */}
      <div className="flex justify-start pt-4">
        <Button variant="outline" onClick={() => navigate("/active")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Active Requests
        </Button>
      </div>
    </div>
  );
};

export default ProposalDetail;
